import { useEffect, useRef, useState } from 'react';
import type { ImageFilter, ImageMask } from '@ox/schema';
import {
  deleteOwnerAsset,
  fetchOwnerAssets,
  loadRecentAssets,
  pushRecentAsset,
  type RecentAsset,
} from '../../lib/recentAssets';
import { oxAuthHeaders } from '../../lib/guestId';
import { removeImageBackground } from '../../lib/removeBackground';
import { uploadImageFile } from '../../lib/uploadAsset';
import { EDITOR_MASKS, maskImageUrl } from '../../lib/masks';
import { EDITOR_FILTERS, resolveImageFilter } from '../../lib/imageFilters';
import type { EditImageResult, PendingImageDraft } from '../EditImageModal';
import { MaskFitPreview, type MaskFitState } from '../MaskFitPreview';
import { FilterThumb } from '../FilterThumb';
import { BottomSheet } from './BottomSheet';

export function UploadSheet({
  apiBase,
  onClose,
  onUpload,
  onPickRecent,
  getToken,
}: {
  apiBase: string;
  onClose: () => void;
  onUpload: (file: File) => Promise<void>;
  onPickRecent: (assetId: string) => void;
  getToken?: () => Promise<string | null | undefined>;
}) {
  const [recent, setRecent] = useState<RecentAsset[]>([]);
  const [busy, setBusy] = useState(false);
  const [copyrightOpen, setCopyrightOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function refreshRecent() {
    const token = getToken ? await getToken() : null;
    const list = await fetchOwnerAssets(apiBase, { token });
    setRecent(list);
  }

  useEffect(() => {
    setRecent(loadRecentAssets());
    void refreshRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  function requestUpload() {
    setCopyrightOpen(true);
  }

  function acceptCopyrightAndPick() {
    setCopyrightOpen(false);
    // Defer so the gate unmounts before the native picker (iOS Safari quirk).
    window.setTimeout(() => fileRef.current?.click(), 50);
  }

  async function handleFile(file: File) {
    setBusy(true);
    try {
      await onUpload(file);
      await refreshRecent();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <BottomSheet title="Cargar tu imagen" onClose={busy ? undefined : onClose} tall>
        <div className="ox-upload-dropzone">
          <p className="ox-upload-dropzone-lead">Arrastra tu imagen aquí</p>
          <p className="ox-upload-dropzone-or">o</p>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (!f) return;
              void handleFile(f);
            }}
          />
          <button
            type="button"
            className="ox-primary ox-m-full-btn ox-upload-cta"
            disabled={busy}
            onClick={requestUpload}
          >
            ↑ Cargar imagen
          </button>
          <p className="ox-hint ox-upload-dropzone-legal">
            Al cargar una imagen aceptas las condiciones de uso y copyright.
          </p>
        </div>
        <p className="ox-hint">
          PNG, JPG, HEIC. Max. 15MB, 7500 px. Resolución mínima recomendada 150 ppp.
        </p>

        <h3 className="ox-m-section-title">Tus imágenes recientes</h3>
        <div className="ox-m-recent-grid">
          {recent.length === 0 && <p className="ox-hint">Aún no hay imágenes recientes.</p>}
          {recent.map((r) => (
            <div key={r.assetId} className="ox-m-recent-item">
              <button
                type="button"
                className="ox-m-recent-thumb"
                disabled={busy}
                onClick={() => onPickRecent(r.assetId)}
              >
                <img src={r.thumbUrl} alt="" />
              </button>
              <button
                type="button"
                className="ox-m-recent-del"
                aria-label="Eliminar"
                disabled={busy}
                onClick={() => {
                  void (async () => {
                    const token = getToken ? await getToken() : null;
                    setRecent((prev) => prev.filter((x) => x.assetId !== r.assetId));
                    await deleteOwnerAsset(apiBase, r.assetId, { token });
                    setRecent(loadRecentAssets());
                  })();
                }}
              >
                🗑
              </button>
            </div>
          ))}
        </div>
        {busy && (
          <div className="ox-upload-busy" role="status" aria-live="polite">
            <div className="ox-upload-busy-card">
              <div className="ox-upload-spinner" aria-hidden />
              <p>Subiendo imagen…</p>
              <p className="ox-hint">Espera un momento; luego podrás editarla.</p>
            </div>
          </div>
        )}
      </BottomSheet>

      {copyrightOpen && (
        <div className="ox-sheet-backdrop ox-copyright-gate" onClick={() => setCopyrightOpen(false)}>
          <div
            className="ox-sheet ox-copyright-sheet"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Condiciones de uso"
          >
            <header className="ox-sheet-header">
              <span className="ox-sheet-title">Condiciones de uso</span>
              <button
                type="button"
                className="ox-sheet-x"
                onClick={() => setCopyrightOpen(false)}
                aria-label="Cerrar"
              >
                ✕
              </button>
            </header>
            <div className="ox-sheet-body">
              <p>
                Al cargar una imagen confirmas que tienes los derechos de uso y copyright, y aceptas
                las condiciones de uso del servicio.
              </p>
            </div>
            <footer className="ox-sheet-footer">
              <button type="button" className="ox-secondary-btn ox-m-full-btn" onClick={() => setCopyrightOpen(false)}>
                Cancelar
              </button>
              <button type="button" className="ox-primary ox-m-full-btn" onClick={acceptCopyrightAndPick}>
                Acepto y continuar
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}

export function EditImageSheet({
  apiBase,
  draft,
  getToken,
  onClose,
  onUse,
  uploading = false,
}: {
  apiBase: string;
  draft: PendingImageDraft;
  getToken: () => Promise<string | null | undefined>;
  onClose: () => void;
  onUse: (result: EditImageResult) => void;
  /** True while the file is still uploading / assetId not ready. */
  uploading?: boolean;
}) {
  const [tab, setTab] = useState<'shapes' | 'bg' | 'effects'>('shapes');
  const [mask, setMask] = useState<ImageMask>(draft.mask ?? 'none');
  const [fit, setFit] = useState<MaskFitState>({
    maskScale: draft.maskScale ?? 1,
    maskOffsetX: draft.maskOffsetX ?? 0,
    maskOffsetY: draft.maskOffsetY ?? 0,
  });
  const [filter, setFilter] = useState<ImageFilter>(
    resolveImageFilter(draft.filter, draft.grayscale),
  );
  const [removeBg, setRemoveBg] = useState(false);
  const [removeInterior, setRemoveInterior] = useState(true);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(draft.previewUrl);
  const [baseUrl, setBaseUrl] = useState(draft.previewUrl);
  const [processedAssetId, setProcessedAssetId] = useState<string | null>(null);

  useEffect(() => {
    setPreviewUrl(draft.previewUrl);
    setBaseUrl(draft.previewUrl);
  }, [draft.previewUrl]);

  async function runRemoveBg() {
    setBusy(true);
    try {
      const blob = await removeImageBackground(baseUrl, { interior: removeInterior });
      const token = await getToken();
      const uploaded = await uploadImageFile(apiBase, blob, {
        filename: 'no-bg.png',
        contentType: 'image/png',
        token: token ?? null,
      });
      setProcessedAssetId(uploaded.assetId);
      setPreviewUrl(
        uploaded.proxyUrl || uploaded.thumbUrl || uploaded.originalUrl || URL.createObjectURL(blob),
      );
    } catch {
      setRemoveBg(false);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!removeBg) {
      setPreviewUrl(baseUrl);
      setProcessedAssetId(null);
      return;
    }
    void runRemoveBg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeBg]);

  const canUse = !busy && !uploading && !!(processedAssetId || draft.assetId);

  return (
    <BottomSheet
      title="Editar imagen"
      onClose={uploading ? undefined : onClose}
      tall
      className="ox-sheet-edit"
      footer={
        <button
          type="button"
          className="ox-primary ox-m-full-btn"
          disabled={!canUse}
          onClick={() =>
            onUse({
              assetId: processedAssetId || draft.assetId,
              widthMm: draft.widthMm,
              heightMm: draft.heightMm,
              mask,
              filter,
              ...(mask === 'none'
                ? { maskScale: 1, maskOffsetX: 0, maskOffsetY: 0 }
                : fit),
            })
          }
        >
          {uploading ? 'Subiendo…' : '✓ Usar imagen'}
        </button>
      }
    >
      <div className={`ox-m-edit-preview ${removeBg ? 'ox-edit-checker' : ''}`}>
        {previewUrl ? (
          <MaskFitPreview
            key={previewUrl}
            src={previewUrl}
            mask={mask}
            filter={filter}
            fit={fit}
            onFitChange={setFit}
          />
        ) : (
          <div className="ox-m-edit-preview-loading" aria-live="polite">
            <div className="ox-upload-spinner" aria-hidden />
            <span>Cargando imagen…</span>
          </div>
        )}
        {uploading && previewUrl ? (
          <div className="ox-m-edit-upload-badge" aria-live="polite">
            Subiendo…
          </div>
        ) : null}
      </div>
      <div className="ox-m-edit-tools">
        <button type="button" className={tab === 'shapes' ? 'active' : ''} onClick={() => setTab('shapes')}>
          Formas con foto
        </button>
        <button type="button" className={tab === 'bg' ? 'active' : ''} onClick={() => setTab('bg')}>
          Quitar fondo
        </button>
        <button type="button" className={tab === 'effects' ? 'active' : ''} onClick={() => setTab('effects')}>
          Efectos <span className="ox-m-new">NEW</span>
        </button>
      </div>
      {tab === 'shapes' && (
        <>
          <p className="ox-hint ox-m-edit-hint">Elige un marco y encuadra la foto</p>
          <div className="ox-m-mask-row">
            {EDITOR_MASKS.map((m) => (
              <button
                key={m.id}
                type="button"
                className={mask === m.id ? 'active' : ''}
                onClick={() => {
                  setMask(m.id);
                  setFit({ maskScale: 1, maskOffsetX: 0, maskOffsetY: 0 });
                }}
              >
                <span
                  className={`ox-m-mask-ico ox-m-mask-${m.id}`}
                  style={
                    maskImageUrl(m.id)
                      ? {
                          WebkitMaskImage: `url(${maskImageUrl(m.id)})`,
                          maskImage: `url(${maskImageUrl(m.id)})`,
                        }
                      : undefined
                  }
                />
                {m.label}
              </button>
            ))}
          </div>
        </>
      )}
      {tab === 'bg' && (
        <>
          <label className="ox-toggle-row">
            <span>Eliminar el fondo de la imagen</span>
            <input
              type="checkbox"
              className="ox-toggle"
              checked={removeBg}
              disabled={busy || uploading}
              onChange={(e) => setRemoveBg(e.target.checked)}
            />
          </label>
          <label className="ox-toggle-row">
            <span>Eliminar también del interior del dibujo</span>
            <input
              type="checkbox"
              className="ox-toggle"
              checked={removeInterior}
              disabled={!removeBg || busy || uploading}
              onChange={(e) => {
                setRemoveInterior(e.target.checked);
                if (removeBg) {
                  setRemoveBg(false);
                  queueMicrotask(() => setRemoveBg(true));
                }
              }}
            />
          </label>
          {removeBg && (
            <button
              type="button"
              className="ox-secondary-btn"
              disabled={busy || uploading}
              onClick={() => void runRemoveBg()}
            >
              Volver a intentarlo
            </button>
          )}
          {busy && <p className="ox-hint">Procesando…</p>}
        </>
      )}
      {tab === 'effects' && (
        <div className="ox-edit-filter-grid ox-m-filter-grid">
          {EDITOR_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={filter === f.id ? 'active' : ''}
              onClick={() => setFilter(f.id)}
            >
              <FilterThumb src={previewUrl} filter={f.id} />
              <span>{f.label}</span>
            </button>
          ))}
        </div>
      )}
    </BottomSheet>
  );
}

/** Call after successful upload to remember asset for the upload sheet. */
export function rememberUploadedAsset(
  apiBase: string,
  assetId: string,
  opts?: { thumbUrl?: string; token?: string | null },
): void {
  if (!assetId?.trim()) return;
  if (opts?.thumbUrl) {
    pushRecentAsset({
      assetId,
      thumbUrl: opts.thumbUrl,
      createdAt: Date.now(),
    });
  }
  const base = apiBase.replace(/\/$/, '');
  fetch(`${base}/assets/${encodeURIComponent(assetId)}`, {
    headers: oxAuthHeaders(opts?.token),
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      if (!data) return;
      pushRecentAsset({
        assetId,
        thumbUrl: data.thumbUrl || data.proxyUrl || data.originalUrl || opts?.thumbUrl || '',
        createdAt: Date.now(),
      });
    })
    .catch(() => {
      /* ignore */
    });
}
